import React, { useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import Card from '../../../../../molecules/Card'
import InlineCardHeader from '../../../../../molecules/Card/InlineCardHeader'
import FieldDisplay from '../../../../../molecules/FieldDisplay'
import SydButton from '../../../../../commonDesign/Button'
import { dataCatalog } from '../../../../../../api/coreData'
import { usePolicy } from '../../../../../../hooks/usePolicy'
import DatasourceDescription from '../shared/DatasourceDescription'
import ModifyDatasourceFlagDialog from '../../dialogs/ModifyDatasourceFlagDialog'

function AiFeatureAttributes ({ datasource }) {
  const isInternalUser = usePolicy('admin_denali_internal')

  const fields = useMemo(() => {
    const target = datasource?.data?.target || {}

    const baseFields = [
      { label: 'ID', value: datasource.catalogDatasourceId },
      { label: 'Name', value: datasource.displayName },
      { label: 'Code', value: datasource.code },
      { label: 'Sub-Type', value: datasource.subType },
      { label: 'Target Service', value: target.service || '—' },
      { label: 'Target Command', value: target.command || '—' },
      { label: 'Target Query', value: target.query || '—' },
      { label: 'Response Shape', value: target.responseShape || 'result' }
    ]

    const internalFields = isInternalUser
      ? [
        { label: 'is System', value: datasource.isSystem ?? false, format: 'bool' },
        { label: 'is Internal', value: datasource.isInternal ?? false, format: 'bool' }
      ]
      : []

    const otherFields = [
      { label: 'Enabled for firm', value: datasource.isAiEnabled ?? false, format: 'bool' },
      { label: 'is Deprecated', value: datasource.isDeprecated ?? false, format: 'bool' }
    ]

    return [...baseFields, ...internalFields, ...otherFields]
  }, [datasource, isInternalUser])

  return (
    <FieldDisplay fields={fields} />
  )
}

AiFeatureAttributes.propTypes = {
  datasource: PropTypes.object
}

function AiFeatureDetailsTab ({ datasource }) {
  const enableDialogRef = useRef(null)
  const deprecatedDialogRef = useRef(null)

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={6}>
        <Card>
          <InlineCardHeader
            action={(
              <>
                <SydButton
                  size='sm'
                  icon='sparkles'
                  onClick={() => enableDialogRef.current.open(datasource)}
                >
                  {datasource.isAiEnabled ? 'Disable' : 'Enable'}
                </SydButton>
                <SydButton
                  size='sm'
                  icon='archive'
                  onClick={() => deprecatedDialogRef.current.open(datasource)}
                >
                  Deprecation
                </SydButton>
              </>
            )}
          >
            Details
          </InlineCardHeader>
          <AiFeatureAttributes datasource={datasource} />
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card>
          <InlineCardHeader>Description</InlineCardHeader>
          <DatasourceDescription datasource={datasource} />
        </Card>
      </Grid>
      <ModifyDatasourceFlagDialog
        ref={enableDialogRef}
        config={{
          title: 'Enable AI Feature',
          propertyName: 'isAiEnabled',
          infoText: 'Enable this AI Feature for everyone in your firm who has access to AI Features. You can turn it off again at any time.',
          getLabel: (value) => value ? 'Enabled for firm' : 'Disabled for firm',
          errorMessage: 'Something unexpected happened while updating the AI Feature enablement',
          mutationHook: () => dataCatalog.useModifyDatasourceAiEnabled()
        }}
      />
      <ModifyDatasourceFlagDialog
        ref={deprecatedDialogRef}
        config={{
          title: 'Deprecate AI Feature',
          propertyName: 'isDeprecated',
          infoText: 'Marking an AI Feature deprecated hides it from the user-facing feature list. Existing automations may continue to call it.',
          getLabel: (value) => value ? 'Deprecated' : 'Active',
          errorMessage: 'Something unexpected happened while updating the deprecated status',
          mutationHook: () => dataCatalog.useModifyDatasourceDeprecated(),
          warningAlert: (
            <Alert severity='warning'>
              Deprecation hides this AI Feature from the user-facing catalog. The proxy will still forward calls from existing automations.
            </Alert>
          )
        }}
      />
    </Grid>
  )
}

AiFeatureDetailsTab.propTypes = {
  datasource: PropTypes.object
}

export default AiFeatureDetailsTab
